import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BehaviorSubject, Subscription, from, of } from 'rxjs';
import { delay, exhaustMap, filter, repeat, tap, withLatestFrom } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { AppSettingsDto, NotificationCategory, NotificationDto, NotificationSectionSettingsDto, NotificationService, NotificationSettingsDto, NotificationToastDto, UserRole } from '../services/api.services';
import { TabMasteryService } from '../services/tab-mastery.service';
import { ToastService } from '../services/toast.service';
import { NotificationToastComponent, NotificationToastDataComponent } from '../shared/components/toast/notification-toast/notification-toast.component';
import { catchAppError } from '../shared/utils';
import { AppSelectors } from '../state/app';
import { mapContentToNotification } from './mappings/notification-mapping-helper';
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';
import { logger } from '../pbir/utils/logger';

const LOGGER = logger('NotificationHandlerService');

const NOTIFICATION_CHECK_INTERVAL = environment.pullRates.notifications;

const NOTIFY_SOUND_PATH = 'assets\\sound\\notification-sound.mp3';
let NOTIF_SOUND: HTMLAudioElement = null;

function ensureNotifSoundLoaded() {
  if (NOTIF_SOUND)
    return;

  try {
    NOTIF_SOUND = new Audio(NOTIFY_SOUND_PATH);
    NOTIF_SOUND.load();
  }
  catch (err) {
    LOGGER.error(`Failed to load notification sound from '${NOTIFY_SOUND_PATH}'.`, err);
  }
}

@Injectable({
  providedIn: 'root'
})
export class NotificationHandlerService implements OnDestroy {
  constructor(
    private notificationService: NotificationService,
    private toastService: ToastService,
    private tabMasteryService: TabMasteryService,
    private store: Store,
    private authService: AuthService
  ) {
    if (isCanvasRendererHost)
      return;
    this._isMasterSub = this.tabMasteryService.isMasterObservable.subscribe((isMaster) => { this.isMultiTabMaster = isMaster });
  }

  ngOnDestroy(): void {
    this._fetchNotificationsSub.unsubscribe();
    this._isMasterSub.unsubscribe();
  }

  private latestToastId = -1;
  private currentToasts: NotificationToastDto[] = [];

  private notifications: NotificationDto[];
  public notificationsObservable: BehaviorSubject<NotificationDto[]> = new BehaviorSubject([]);

  private notifSound: HTMLAudioElement;
  private appSettings: AppSettingsDto;
  private _fetchNotificationsSub: Subscription = null;

  private isMultiTabMaster: boolean;
  private _isMasterSub: Subscription;

  public initNotificationHandler() {
    this.notificationService.purgeTimedOutToasts().pipe(
      catchAppError(null, { showErrorSnack: false })
    ).subscribe();

    this._fetchNotificationsSub =
      of(null).pipe(
        filter(() => NOTIFICATION_CHECK_INTERVAL != null),

        withLatestFrom(
          this.store.select(AppSelectors.getSettings),
          this.store.select(AppSelectors.getUser)),

        tap(([, appSettings]) => {
          if (appSettings)
            this.appSettings = appSettings;
        }),

        exhaustMap(([, appSettings, user]) => {
          // Must be auth'ed, need settings and user must be > BU
          if (this.authService.loggedIn
            && !!appSettings
            && user
            && user.role > UserRole.ClientUser
            && this.authService.ready)
            return from(this.fetchNotifications()).pipe(
              // Don't propagate errors, so we can retry
              catchAppError(null, { showErrorSnack: false })
            );

          return of(null);
        }),
        delay(NOTIFICATION_CHECK_INTERVAL),
        repeat(),
      ).subscribe();
  }

  private async fetchNotifications() {
    const notifInfo = await this.notificationService.getNotificationInfo(this.latestToastId, this.getStartDate(), 0).toPromise();

    for (const toast of notifInfo.pendingToasts) {
      const { enabled, section } = IsNotificationSectionEnabled(this.appSettings.notificationSettings, toast.category)

      if (enabled && section.toastsEnabled) {
        this.currentToasts.push(toast);
        this.displayToast(toast);
      }
    }

    if (notifInfo.pendingToasts.length > 0) {
      this.latestToastId = Math.max(...notifInfo.pendingToasts.map(t => t.id));

      if (this.isSoundNeeded(notifInfo.pendingToasts)) {
        ensureNotifSoundLoaded();
        NOTIF_SOUND?.play();
      }
    }

    if (JSON.stringify(this.notifications) != JSON.stringify(notifInfo.notifications)) {
      this.notifications = notifInfo.notifications;
      this.notificationsObservable.next(notifInfo.notifications);
    }
  }

  private getStartDate(): string {
    const date = moment.tz(this.appSettings.timeZoneIana).startOf('day').format();
    return date
  }

  private displayToast(notif: NotificationToastDto) {
    const basicnotif = mapContentToNotification(notif.content, null);

    const toast = this.toastService.open({ component: NotificationToastComponent, data: <NotificationToastDataComponent>{ notification: basicnotif, userCanDismiss: true }, dismissAfter: 5000, id: notif.id });

    toast.dismissalSubject.subscribe((info) => {
      const index = this.currentToasts.indexOf(notif);
      this.currentToasts.splice(index, 1);

      if (!info.dismissedByBroadcast)
        this.notificationService.markNotificationToastComplete(notif.id, info.autoDismissed).subscribe();
    });
  }

  private isSoundNeeded(toasts: NotificationToastDto[]) {
    if (!this.isMultiTabMaster)
      return;

    const categorys = toasts.map(t => t.category)

    if (!this.appSettings.notificationSettings.sections[0].soundsEnabled)
      return false;

    for (const section of this.appSettings.notificationSettings.sections) {
      if (!section.soundsEnabled)
        continue;

      if (categorys.includes(section.category))
        return true;
    }

  }
}

export function IsNotificationSectionEnabled(settings: NotificationSettingsDto, category: NotificationCategory): { enabled: boolean, section: NotificationSectionSettingsDto } {
  if (!settings?.notificationsEnabled)
    return { enabled: false, section: null };

  const allSection = settings.sections.find(s => s.category == NotificationCategory.All);

  if (!allSection.panelEnabled && !allSection.toastsEnabled)
    return { enabled: false, section: null };

  const section = settings.sections.find(s => s.category == category);

  if (!(section.panelEnabled && allSection.panelEnabled) && !(section.toastsEnabled && allSection.toastsEnabled))
    return { enabled: false, section: null };


  return { enabled: true, section: section };
}
